import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Btn, Field, TextInput, TextArea, SelectInput, EmptyState } from "../components/Primitives";
import { toast } from "sonner";
import { ShieldCheck, Search, Mail, KeyRound, ShieldOff, RefreshCw, Download } from "lucide-react";

const REQ_TYPES = [
  { v: "access", l: "Access my data (Sec. 11)" },
  { v: "correction", l: "Correct my data (Sec. 12)" },
  { v: "update", l: "Update my data (Sec. 12)" },
  { v: "erasure", l: "Erase my data (Sec. 12)" },
  { v: "consent_withdrawal", l: "Withdraw consent (Sec. 6)" },
  { v: "grievance", l: "Raise a grievance (Sec. 13)" },
  { v: "nomination", l: "Nominee-related (Sec. 14)" },
  { v: "clarification", l: "Data-processing clarification (Sec. 5)" },
];

export default function DataPrincipalPortal() {
  const [tenants, setTenants] = useState([]);
  const [tab, setTab] = useState("submit");
  // OTP-guarded submit flow: 1) enter details, 2) OTP sent, 3) verify + submit
  const [step, setStep] = useState("form"); // form | otp | done
  const [form, setForm] = useState({ tenant_id: "", request_type: "access", principal_name: "", principal_email: "", principal_phone: "", description: "" });
  const [otp, setOtp] = useState("");
  const [otpRef, setOtpRef] = useState("");
  const [devOtp, setDevOtp] = useState(""); // dev-mode aid
  const [verificationToken, setVerificationToken] = useState("");
  const [submitted, setSubmitted] = useState(null);
  const [busy, setBusy] = useState(false);

  const [trackId, setTrackId] = useState("");
  const [trackTenant, setTrackTenant] = useState("");
  const [trackEmail, setTrackEmail] = useState("");
  const [trackResult, setTrackResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [escReason, setEscReason] = useState("");

  // Consent portal: OTP-verified access to a principal's own consents
  const [cStep, setCStep] = useState("form"); // form | otp | list
  const [cForm, setCForm] = useState({ tenant_id: "", email: "" });
  const [cOtp, setCOtp] = useState("");
  const [cRef, setCRef] = useState("");
  const [cDev, setCDev] = useState("");
  const [cToken, setCToken] = useState("");
  const [cConsents, setCConsents] = useState([]);
  const [cHistory, setCHistory] = useState([]);
  const [cBusy, setCBusy] = useState(false);

  useEffect(() => {
    api.get("/portal/tenants").then(r => {
      setTenants(r.data);
      if (r.data.length > 0 && !form.tenant_id) setForm(f => ({...f, tenant_id: r.data[0].id}));
      if (r.data.length > 0 && !trackTenant) setTrackTenant(r.data[0].id);
      if (r.data.length > 0 && !cForm.tenant_id) setCForm(f => ({...f, tenant_id: r.data[0].id}));
    });
     
  }, []);

  // -------- Consent portal handlers --------
  const cSendOtp = async (e) => {
    e.preventDefault();
    if (!cForm.email) { toast.error("Email required"); return; }
    setCBusy(true);
    try {
      const { data } = await api.post("/portal/otp/send", { email: cForm.email, purpose: "consent_portal", tenant_id: cForm.tenant_id });
      setCRef(data.ref); setCDev(data.dev_otp || ""); setCStep("otp");
      toast.success("OTP sent");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setCBusy(false);
  };
  const cVerify = async (e) => {
    e.preventDefault();
    setCBusy(true);
    try {
      const { data: v } = await api.post("/portal/otp/verify", { ref: cRef, email: cForm.email, otp: cOtp });
      setCToken(v.verification_token);
      const [{ data: cRes }, { data: hRes }] = await Promise.all([
        api.get(`/portal/consents?tenant_id=${cForm.tenant_id}&email=${encodeURIComponent(cForm.email)}&verification_token=${v.verification_token}`),
        api.get(`/portal/consents/history?tenant_id=${cForm.tenant_id}&email=${encodeURIComponent(cForm.email)}&verification_token=${v.verification_token}`),
      ]);
      setCConsents(cRes.items || []);
      setCHistory(hRes.items || []);
      setCStep("list");
    } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed"); }
    setCBusy(false);
  };
  const cWithdraw = async (cItem) => {
    const reason = window.prompt("Why are you withdrawing? (optional)", "");
    try {
      await api.post("/portal/consents/withdraw", {
        tenant_id: cForm.tenant_id, email: cForm.email, verification_token: cToken,
        consent_id: cItem.id, reason,
      });
      toast.success("Consent withdrawn — deletion task raised.");
      const { data } = await api.get(`/portal/consents?tenant_id=${cForm.tenant_id}&email=${encodeURIComponent(cForm.email)}&verification_token=${cToken}`);
      setCConsents(data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Withdraw failed"); }
  };
  const cRenew = async (cItem) => {
    try {
      await api.post("/portal/consents/renew", {
        tenant_id: cForm.tenant_id, email: cForm.email, verification_token: cToken,
        consent_id: cItem.id,
      });
      toast.success("Consent renewed on latest template version.");
      const { data } = await api.get(`/portal/consents?tenant_id=${cForm.tenant_id}&email=${encodeURIComponent(cForm.email)}&verification_token=${cToken}`);
      setCConsents(data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Renew failed"); }
  };
  const cDownloadHistory = () => {
    const blob = new Blob([JSON.stringify({ email: cForm.email, tenant_id: cForm.tenant_id, consents: cConsents, history: cHistory }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `consent_history_${cForm.email}.json`; a.click();
    URL.revokeObjectURL(url);
  };
  const cGrievance = async (cItem) => {
    const description = window.prompt("Describe your grievance:", `Grievance regarding consent for ${cItem.purpose_name || cItem.template_id}`);
    if (!description) return;
    try {
      await api.post("/portal/requests", {
        tenant_id: cForm.tenant_id, request_type: "grievance",
        principal_email: cForm.email, principal_id: cForm.email,
        description, verification_token: cToken,
      });
      toast.success("Grievance filed. The DPO has been notified.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Grievance filing failed"); }
  };

  const sendOtp = async (e) => {
    e.preventDefault();
    if (!form.principal_email) { toast.error("Email required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/portal/otp/send", { email: form.principal_email, purpose: "portal", tenant_id: form.tenant_id });
      setOtpRef(data.ref);
      setDevOtp(data.dev_otp || "");
      setStep("otp");
      toast.success("OTP sent to your email");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to send OTP"); }
    setBusy(false);
  };

  const verifyAndSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: v } = await api.post("/portal/otp/verify", { ref: otpRef, email: form.principal_email, otp });
      const token = v.verification_token;
      setVerificationToken(token);
      const { data } = await api.post("/portal/requests", { ...form, verification_token: token });
      setSubmitted(data);
      setStep("done");
      toast.success("Request submitted");
    } catch (err) { toast.error(err?.response?.data?.detail || "Verification failed"); }
    setBusy(false);
  };

  const resetFlow = () => {
    setSubmitted(null); setStep("form"); setOtp(""); setOtpRef(""); setDevOtp(""); setVerificationToken("");
    setForm({...form, description: ""});
  };

  const track = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.get(`/portal/track/${trackId}`, { params: { tenant_id: trackTenant }});
      setTrackResult(data);
    } catch { toast.error("Not found"); }
  };

  const uploadAttachment = async (file) => {
    if (!trackResult || !trackEmail) { toast.error("Please provide the email you used to submit."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tenant_id", trackTenant);
      fd.append("principal_email", trackEmail);
      await api.post(`/portal/requests/${trackResult.id}/attach`, fd);
      toast.success("Document uploaded");
      const { data } = await api.get(`/portal/track/${trackResult.id}`, { params: { tenant_id: trackTenant }});
      setTrackResult(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };

  const downloadResponse = async () => {
    if (!trackEmail) { toast.error("Please provide your email to authorize the download."); return; }
    try {
      const res = await api.get(`/portal/requests/${trackResult.id}/response-document`, {
        params: { tenant_id: trackTenant, principal_email: trackEmail },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = trackResult.response_document?.filename || "response";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err?.response?.data?.detail || "Download failed"); }
  };

  const escalate = async () => {
    if (!trackEmail || !escReason) { toast.error("Email + reason required"); return; }
    try {
      await api.post(`/portal/requests/${trackResult.id}/escalate`, {
        tenant_id: trackTenant, principal_email: trackEmail, reason: escReason,
      });
      toast.success("Escalated to DPO");
      setEscReason("");
      const { data } = await api.get(`/portal/track/${trackResult.id}`, { params: { tenant_id: trackTenant }});
      setTrackResult(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Escalation failed"); }
  };

  return (
    <div className="min-h-screen bg-app">
      <div className="surface" style={{ borderBottom: "1px solid var(--border)" }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)" }}>
              <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <div className="font-semibold ink text-sm leading-tight">Data Principal Portal</div>
              <div className="text-[10px] uppercase tracking-widest muted mono">DPDP Act 2023 · Public Counter</div>
            </div>
          </div>
          <a href="/login" data-testid="portal-back-to-app" className="text-xs muted hover:ink">Staff sign-in →</a>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-6 flex gap-6" style={{ borderBottom: "1px solid var(--border)" }}>
          {["submit", "track", "consents"].map(t => (
            <button
              key={t}
              data-testid={`portal-tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-1 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t ? "ink" : "muted hover:ink"}`}
              style={{ borderBottomColor: tab === t ? "var(--primary)" : "transparent" }}
            >
              {t === "submit" ? "Submit request" : t === "track" ? "Track request" : "My consents"}
            </button>
          ))}
        </div>

        {tab === "submit" && step === "form" && (
          <form onSubmit={sendOtp} className="surface rounded-2xl p-6 lg:p-8 card-shadow space-y-4" style={{ border: "1px solid var(--border)" }}>
            <div>
              <div className="label-cap mb-2">Article 11 · Rights</div>
              <div className="text-2xl font-semibold ink tracking-tight">Submit a request under DPDP Act</div>
              <div className="text-sm muted mt-1.5">Exercise your rights to access, correct, or erase your personal data. Identity is verified via a one-time email code.</div>
            </div>
            <Field label="Organization" required>
              <SelectInput data-testid="portal-tenant" value={form.tenant_id} onChange={(e) => setForm({...form, tenant_id: e.target.value})} required>
                <option value="">-- Select --</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </SelectInput>
            </Field>
            <Field label="Request type" required>
              <SelectInput data-testid="portal-request-type" value={form.request_type} onChange={(e) => setForm({...form, request_type: e.target.value})} required>
                {REQ_TYPES.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </SelectInput>
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Your name" required><TextInput data-testid="portal-name" value={form.principal_name} onChange={(e) => setForm({...form, principal_name: e.target.value})} required /></Field>
              <Field label="Email" required><TextInput data-testid="portal-email" type="email" value={form.principal_email} onChange={(e) => setForm({...form, principal_email: e.target.value})} required /></Field>
              <Field label="Phone" className="sm:col-span-2"><TextInput data-testid="portal-phone" value={form.principal_phone} onChange={(e) => setForm({...form, principal_phone: e.target.value})} /></Field>
            </div>
            <Field label="Description / details"><TextArea data-testid="portal-description" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Please describe your request..." /></Field>
            <Btn type="submit" className="w-full justify-center" disabled={busy} data-testid="portal-submit-btn">
              <Mail className="w-4 h-4" /> {busy ? "Sending OTP..." : "Send verification code"}
            </Btn>
            <div className="text-[11px] text-slate-500">A 6-digit code will be emailed to verify your identity. In demo mode, the code is also shown on-screen.</div>
          </form>
        )}

        {tab === "submit" && step === "otp" && (
          <form onSubmit={verifyAndSubmit} className="surface rounded-2xl p-6 lg:p-8 card-shadow space-y-4" style={{ border: "1px solid var(--border)" }}>
            <div>
              <div className="label-cap mb-2">Step 2 of 2 · Verify</div>
              <div className="text-2xl font-semibold ink tracking-tight">Verify your email</div>
              <div className="text-sm muted mt-1.5">Enter the 6-digit code sent to <strong className="ink">{form.principal_email}</strong>.</div>
            </div>
            {devOtp && (
              <div data-testid="portal-dev-otp" className="rounded-lg px-4 py-3 text-sm flex items-center gap-3" style={{ background: "var(--warn-soft)", color: "var(--warn)", border: "1px solid #FDE68A" }}>
                <KeyRound className="w-4 h-4" />
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest">Demo mode OTP</div>
                  <div className="font-mono text-lg font-bold ink">{devOtp}</div>
                </div>
              </div>
            )}
            <Field label="6-digit code" required>
              <TextInput data-testid="portal-otp" value={otp} onChange={(e) => setOtp(e.target.value)} maxLength={6} required placeholder="123456" />
            </Field>
            <div className="flex gap-2">
              <Btn type="button" variant="secondary" onClick={() => setStep("form")}>Back</Btn>
              <Btn type="submit" disabled={busy} data-testid="portal-verify-submit-btn"><KeyRound className="w-4 h-4" /> {busy ? "Verifying..." : "Verify & submit request"}</Btn>
            </div>
          </form>
        )}

        {tab === "submit" && step === "done" && submitted && (
          <div className="surface rounded-2xl p-6 lg:p-8 card-shadow" style={{ border: "1px solid var(--border)" }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ background: "var(--success-soft)", color: "var(--success)" }}>
              <ShieldCheck className="w-3.5 h-3.5" /> Request received
            </div>
            <div className="text-2xl font-semibold ink tracking-tight">Your request has been filed</div>
            <div className="mt-2 text-sm muted">Save this reference number to track your request:</div>
            <div data-testid="submitted-request-id" className="mt-3 rounded-lg px-4 py-3 mono text-sm ink" style={{ background: "var(--bg-2)", border: "1px solid var(--border)" }}>{submitted.id}</div>
            <div className="text-xs muted mt-4">
              Organization: <strong className="ink">{tenants.find(t => t.id === submitted.tenant_id)?.name}</strong><br/>
              Identity verified via OTP · Response due by <strong className="ink">{submitted.due_by?.slice(0, 10)}</strong>.
            </div>
            <Btn className="mt-5" data-testid="portal-new-request" onClick={resetFlow}>Submit another request</Btn>
          </div>
        )}

        {tab === "track" && (
          <>
            <form onSubmit={track} className="surface rounded-2xl p-6 lg:p-8 card-shadow space-y-4" style={{ border: "1px solid var(--border)" }}>
              <div>
                <div className="label-cap mb-2">Track</div>
                <div className="text-2xl font-semibold ink tracking-tight">Track your request</div>
                <div className="text-sm muted mt-1">See the current status, upload documents, download the response, or escalate.</div>
              </div>
              <Field label="Organization" required>
                <SelectInput data-testid="track-tenant" value={trackTenant} onChange={(e) => setTrackTenant(e.target.value)} required>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </SelectInput>
              </Field>
              <Field label="Request reference ID" required><TextInput data-testid="track-id" value={trackId} onChange={(e) => setTrackId(e.target.value)} required /></Field>
              <Field label="Your email (used at submission)"><TextInput data-testid="track-email" type="email" value={trackEmail} onChange={(e) => setTrackEmail(e.target.value)} placeholder="Required for actions like upload / escalate / download" /></Field>
              <Btn type="submit" data-testid="track-submit-btn"><Search className="w-3.5 h-3.5" /> Track</Btn>
            </form>

            {trackResult && (
              <div data-testid="track-result" className="mt-4 space-y-3">
                <div className="surface rounded-2xl p-6 card-shadow space-y-3" style={{ border: "1px solid var(--border)" }}>
                  <div className="grid grid-cols-2 gap-4">
                    <div><span className="label-cap">Type</span><div className="text-base font-medium ink capitalize mt-1">{trackResult.request_type.replace(/_/g, " ")}</div></div>
                    <div><span className="label-cap">Status</span><div className="text-base font-medium ink capitalize mt-1">{trackResult.status?.replace(/_/g, " ")}</div></div>
                    <div><span className="label-cap">Verification</span><div className="text-base font-medium ink capitalize mt-1">{trackResult.verification_status}</div></div>
                    <div><span className="label-cap">Due by</span>
                      <div className="text-base font-medium ink mt-1">{trackResult.due_by?.slice(0, 10)}
                        {trackResult.sla_state && (
                          <span className="ml-2 text-[10.5px] font-medium px-1.5 py-0.5 rounded" style={{
                            background: trackResult.sla_state === "breached" ? "var(--danger-soft)" : trackResult.sla_state === "at_risk" ? "var(--warn-soft)" : "var(--success-soft)",
                            color: trackResult.sla_state === "breached" ? "var(--danger)" : trackResult.sla_state === "at_risk" ? "var(--warn)" : "var(--success)",
                          }}>{trackResult.sla_state.replace(/_/g, " ")}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="pt-3" style={{ borderTop: "1px solid var(--border)" }}>
                    <span className="label-cap">Response</span>
                    <div className="text-sm ink mt-1 whitespace-pre-wrap">{trackResult.response_summary || "— pending —"}</div>
                    {trackResult.response_document && (
                      <div className="mt-3">
                        <Btn variant="secondary" onClick={downloadResponse} data-testid="track-download-response">
                          <Download className="w-3.5 h-3.5" /> Download response document
                        </Btn>
                      </div>
                    )}
                  </div>
                </div>

                {/* Info requested */}
                {(trackResult.info_requested || []).length > 0 && (
                  <div className="surface rounded-2xl p-6 card-shadow" style={{ border: "1px solid var(--warn)" }} data-testid="track-info-required">
                    <div className="label-cap mb-2" style={{ color: "var(--warn)" }}>Additional information required</div>
                    {trackResult.info_requested.map(ir => (
                      <div key={ir.id} className="mb-2">
                        <div className="text-[13px] ink">{ir.message}</div>
                        <ul className="list-disc pl-4 text-[12px] muted mt-1">
                          {(ir.items || []).map((it, i) => <li key={i}>{it}</li>)}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {/* Attachments */}
                <div className="surface rounded-2xl p-6 card-shadow" style={{ border: "1px solid var(--border)" }}>
                  <div className="label-cap mb-2">Your uploaded documents</div>
                  {(trackResult.attachments || []).length === 0 ? (
                    <div className="text-sm muted">No documents uploaded yet.</div>
                  ) : (
                    <div className="space-y-1.5" data-testid="track-attachments">
                      {trackResult.attachments.map(a => (
                        <div key={a.id} className="flex items-center gap-2 p-2 rounded-md" style={{ background: "var(--bg-2)" }}>
                          <Download className="w-3.5 h-3.5" />
                          <div className="flex-1 text-[12px] ink truncate">{a.filename}</div>
                          <div className="text-[10.5px] muted">{Math.round((a.size || 0) / 1024)} KB</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer text-[11.5px] font-medium mt-3"
                    style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>
                    {uploading ? "Uploading..." : "Upload supporting document"}
                    <input type="file" data-testid="track-upload" className="hidden" disabled={uploading}
                      onChange={(e) => e.target.files?.[0] && uploadAttachment(e.target.files[0])} />
                  </label>
                </div>

                {/* Timeline */}
                {(trackResult.activity_log || []).length > 0 && (
                  <div className="surface rounded-2xl p-6 card-shadow" style={{ border: "1px solid var(--border)" }}>
                    <div className="label-cap mb-2">Progress timeline</div>
                    <div className="space-y-1.5" data-testid="track-timeline">
                      {trackResult.activity_log.slice().reverse().map(ev => (
                        <div key={ev.id} className="flex items-start gap-2 p-2 rounded-md" style={{ background: "var(--bg-2)" }}>
                          <div className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: "var(--primary)" }} />
                          <div className="flex-1">
                            <div className="text-[12px] ink"><b>{ev.action.replace(/_/g, " ")}</b></div>
                            <div className="text-[10.5px] muted">{ev.at?.slice(0, 19).replace("T", " ")}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Escalate */}
                {!["completed", "closed"].includes(trackResult.status) && (
                  <div className="surface rounded-2xl p-6 card-shadow" style={{ border: "1px solid var(--danger)", background: "var(--danger-soft)" }}>
                    <div className="label-cap mb-2" style={{ color: "var(--danger)" }}>Not satisfied? Escalate to DPO</div>
                    <TextArea rows={2} value={escReason} onChange={(e) => setEscReason(e.target.value)}
                      placeholder="Reason for escalation" data-testid="track-esc-reason" />
                    <Btn className="mt-2" onClick={escalate} disabled={!escReason || !trackEmail} data-testid="track-esc-btn">
                      <ShieldOff className="w-3.5 h-3.5" /> Escalate
                    </Btn>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {tab === "consents" && cStep === "form" && (
          <form onSubmit={cSendOtp} className="surface rounded-2xl p-6 lg:p-8 card-shadow space-y-4" style={{ border: "1px solid var(--border)" }}>
            <div>
              <div className="label-cap mb-2">Sec. 6 · Consent Portal</div>
              <div className="text-2xl font-semibold ink tracking-tight">Manage your consents</div>
              <div className="text-sm muted mt-1.5">View, withdraw or renew any consent you&apos;ve given. We verify your identity via a one-time email code.</div>
            </div>
            <Field label="Organization" required>
              <SelectInput data-testid="cportal-tenant" value={cForm.tenant_id} onChange={(e) => setCForm({...cForm, tenant_id: e.target.value})} required>
                <option value="">-- Select --</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </SelectInput>
            </Field>
            <Field label="Your email" required>
              <TextInput data-testid="cportal-email" type="email" value={cForm.email} onChange={(e) => setCForm({...cForm, email: e.target.value})} required />
            </Field>
            <Btn type="submit" className="w-full justify-center" disabled={cBusy} data-testid="cportal-send-otp">
              <Mail className="w-4 h-4" /> {cBusy ? "Sending OTP..." : "Send verification code"}
            </Btn>
          </form>
        )}

        {tab === "consents" && cStep === "otp" && (
          <form onSubmit={cVerify} className="surface rounded-2xl p-6 lg:p-8 card-shadow space-y-4" style={{ border: "1px solid var(--border)" }}>
            <div className="text-2xl font-semibold ink">Verify your email</div>
            <div className="text-sm muted">Code sent to <strong className="ink">{cForm.email}</strong></div>
            {cDev && (
              <div data-testid="cportal-dev-otp" className="rounded-lg px-4 py-3 text-sm" style={{ background: "var(--warn-soft)", color: "var(--warn)" }}>
                <div className="text-[10px] font-semibold uppercase tracking-widest">Demo mode OTP</div>
                <div className="font-mono text-lg font-bold ink">{cDev}</div>
              </div>
            )}
            <Field label="6-digit code" required>
              <TextInput data-testid="cportal-otp" value={cOtp} onChange={(e) => setCOtp(e.target.value)} maxLength={6} required />
            </Field>
            <div className="flex gap-2">
              <Btn type="button" variant="secondary" onClick={() => setCStep("form")}>Back</Btn>
              <Btn type="submit" disabled={cBusy} data-testid="cportal-verify"><KeyRound className="w-4 h-4" /> {cBusy ? "Verifying..." : "Access my consents"}</Btn>
            </div>
          </form>
        )}

        {tab === "consents" && cStep === "list" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-2xl font-semibold ink">My consents</div>
                <div className="text-sm muted">{cForm.email} · {cConsents.length} on record</div>
              </div>
              <div className="flex gap-2">
                <Btn variant="secondary" onClick={cDownloadHistory} data-testid="cportal-download-history">
                  <Download className="w-4 h-4" /> Download history
                </Btn>
                <Btn variant="secondary" onClick={() => { setCStep("form"); setCConsents([]); setCHistory([]); setCToken(""); }}>Sign out</Btn>
              </div>
            </div>
            {cConsents.length === 0 ? (
              <EmptyState title="No consents on record" hint="We couldn&apos;t find any consent linked to this email." />
            ) : (
              <div className="grid gap-3" data-testid="cportal-consents-list">
                {cConsents.map((c) => (
                  <div key={c.id} className="surface rounded-xl p-4" style={{ border: "1px solid var(--border)" }} data-testid={`cportal-consent-${c.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-semibold ink text-[14px]">{c.purpose_name || "Consent"}</div>
                          <span className="px-2 py-[3px] rounded-md text-[10.5px] font-medium" style={{
                            background: c.status === "active" ? "var(--success-soft)" : c.status === "withdrawn" ? "var(--danger-soft)" : "var(--bg-2)",
                            color: c.status === "active" ? "var(--success)" : c.status === "withdrawn" ? "var(--danger)" : "var(--muted)",
                          }}>{c.status.replace(/_/g, " ")}</span>
                          <span className="text-[11px] muted">v{c.template_version}</span>
                        </div>
                        <div className="text-[11px] muted mt-1 flex items-center gap-3 flex-wrap">
                          <span>Source: {c.source_application || "—"}</span>
                          <span>Granted: {c.granted_at?.slice(0, 10)}</span>
                          {c.expires_at && <span>Expires: {c.expires_at.slice(0, 10)}</span>}
                        </div>
                        {(c.data_categories || []).length > 0 && (
                          <div className="text-[11px] muted mt-1">Data: {(c.data_categories || []).join(", ")}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {["active", "pending_renewal"].includes(c.status) && (
                          <Btn variant="secondary" size="sm" onClick={() => cWithdraw(c)} data-testid={`cportal-withdraw-${c.id}`}>
                            <ShieldOff className="w-3.5 h-3.5" /> Withdraw
                          </Btn>
                        )}
                        {["pending_renewal", "replaced_by_newer_version"].includes(c.status) && (
                          <Btn size="sm" onClick={() => cRenew(c)} data-testid={`cportal-renew-${c.id}`}>
                            <RefreshCw className="w-3.5 h-3.5" /> Renew
                          </Btn>
                        )}
                        <button onClick={() => cGrievance(c)} className="text-[11px] font-medium hover:underline" style={{ color: "var(--danger)" }} data-testid={`cportal-grievance-${c.id}`}>
                          Grievance
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {cHistory.length > 0 && (
              <div className="surface rounded-xl p-4" style={{ border: "1px solid var(--border)" }}>
                <div className="text-[13px] font-semibold ink mb-2">Audit trail ({cHistory.length} events)</div>
                <div className="text-[11px] muted mono max-h-64 overflow-y-auto space-y-1">
                  {cHistory.slice(0, 30).map((e, i) => (
                    <div key={i}>{e.created_at?.slice(0, 19).replace("T", " ")} · <b>{e.action}</b> · {e.actor_type} · hash {e.proof_hash?.slice(0, 12)}…</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-8 text-[11px] muted leading-relaxed" style={{ borderTop: "1px solid var(--border)", paddingTop: 16 }}>
          <strong className="ink font-semibold">Legal Disclaimer.</strong> This portal facilitates exercise of your rights under
          India&apos;s Digital Personal Data Protection Act, 2023. It does not constitute legal advice.
        </div>
      </div>
    </div>
  );
}